import { useCallback, useEffect, useMemo, useState } from "react";
import "./GaReportPage.css";
import useApiBase from "./api/useApiBase";
import AuthGate from "./auth/AuthGate";
import useAuthSession from "./auth/useAuthSession";
import { resolveLineTimezone } from "./siteTime";
import usePageTitle from "./usePageTitle";

/** Today as YYYY-MM-DD at the site, not in whoever's browser is open. */
const todayIso = (timeZone) =>
  new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date());

const DAY_START = "T00:00:00";
const DAY_END = "T23:59:59";

/** datetime-local drops ":00" seconds; send the backend a full HH:MM:SS. */
const withSeconds = (value) => (/T\d{2}:\d{2}$/.test(value) ? `${value}:00` : value);

const downloadName = (response, fallback) => {
  const header = response.headers.get("Content-Disposition") || "";
  const match = header.match(/filename="([^"]+)"/);
  return match ? match[1] : fallback;
};

const readLineFromQuery = () => {
  if (typeof window === "undefined") {
    return "";
  }
  const value = new URLSearchParams(window.location.search).get("line_no");
  return value && /^\d+$/.test(value) ? value : "";
};

const formatNumber = (value, digits) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return "—";
  }
  return parsed.toFixed(digits);
};

const formatPercent = (value, digits) => {
  const text = formatNumber(value, digits);
  return text === "—" ? text : `${text}%`;
};

const diffClass = (value, tolerance) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return "";
  }
  return Math.abs(parsed) > tolerance ? "ga-diff-alert" : "ga-diff-ok";
};

const FlagCell = ({ value }) => (
  <td className={Number(value) ? "ga-flag-cell ga-flag-on" : "ga-flag-cell"}>
    {Number(value) ? "True" : "False"}
  </td>
);

const ReportTable = ({ rows }) => (
  <div className="ga-table-scroll">
    <table className="ga-table">
      <thead>
        <tr>
          <th rowSpan="2">SKU</th>
          <th rowSpan="2">Orders</th>
          <th rowSpan="2">Nominal weight</th>
          <th rowSpan="2">Expected weight</th>
          <th colSpan="3">Packs</th>
          <th colSpan="3">Actual weight</th>
          <th colSpan="3">Giveaway kilograms</th>
          <th colSpan="3">Giveaway percentage</th>
          <th colSpan="2">Rejection rate percentage</th>
          <th colSpan="2">Rejected kilograms</th>
          <th rowSpan="2">Pack delay</th>
          <th rowSpan="2">Vemag delay</th>
        </tr>
        <tr>
          <th>Database</th>
          <th>Scheduler</th>
          <th>Difference</th>
          <th>Database</th>
          <th>Scheduler</th>
          <th>Difference</th>
          <th>Database</th>
          <th>Scheduler</th>
          <th>Difference</th>
          <th>Database</th>
          <th>Scheduler</th>
          <th>Difference</th>
          <th>Database</th>
          <th>Scheduler</th>
          <th>Database</th>
          <th>Scheduler</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.date}-${row.sku}`}>
            <td className="ga-sku">
              {row.sku}
              {row.scheduler_source && row.scheduler_source !== "order_totals" ? (
                <span
                  className="ga-flag ga-flag-warn"
                  title={`Scheduler side ${row.scheduler_source} — not read from stored order totals`}
                >
                  {row.scheduler_source}
                </span>
              ) : null}
            </td>
            <td className="ga-orders">
              {(row.orders || []).join(", ")}
              {row.multiple_orders ? (
                <span className="ga-flag" title="Several orders summed into this row">
                  multiple_orders
                </span>
              ) : null}
            </td>
            <td>{formatNumber(row.nominal_weight, 3)}</td>
            <td>{formatNumber(row.expected_weight, 3)}</td>
            <td>{row.packs_database}</td>
            <td>{row.packs_scheduler}</td>
            <td className={diffClass(row.packs_difference, 0)}>{row.packs_difference}</td>
            <td>{formatNumber(row.actual_weight_database, 3)}</td>
            <td>{formatNumber(row.actual_weight_scheduler, 3)}</td>
            <td className={diffClass(row.actual_weight_difference, 0.001)}>
              {formatNumber(row.actual_weight_difference, 3)}
            </td>
            <td>{formatNumber(row.giveaway_kilograms_database, 3)}</td>
            <td>{formatNumber(row.giveaway_kilograms_scheduler, 3)}</td>
            <td className={diffClass(row.giveaway_kilograms_difference, 0.001)}>
              {formatNumber(row.giveaway_kilograms_difference, 3)}
            </td>
            <td>{formatPercent(row.giveaway_percentage_database, 4)}</td>
            <td>{formatPercent(row.giveaway_percentage_scheduler, 4)}</td>
            <td className={diffClass(row.giveaway_percentage_difference, 0.0001)}>
              {formatNumber(row.giveaway_percentage_difference, 4)}
            </td>
            <td>{formatPercent(row.rejection_rate_percentage_database, 4)}</td>
            <td>{formatPercent(row.rejection_rate_percentage_scheduler, 4)}</td>
            <td>{formatNumber(row.rejected_kilograms_database, 3)}</td>
            <td>{formatNumber(row.rejected_kilograms_scheduler, 3)}</td>
            <FlagCell value={row.pack_delay} />
            <FlagCell value={row.vemag_delay} />
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

function GaReportPage() {
  usePageTitle("Giveaway Report");
  const apiBase = useApiBase();
  const auth = useAuthSession(apiBase);

  const [lineNo, setLineNo] = useState(readLineFromQuery);
  const [dateFrom, setDateFrom] = useState(() => `${todayIso()}${DAY_START}`);
  const [dateTo, setDateTo] = useState(() => `${todayIso()}${DAY_END}`);
  const [datesTouched, setDatesTouched] = useState(false);
  const [configPayload, setConfigPayload] = useState(null);
  const [gapThreshold, setGapThreshold] = useState("60");
  const [lineOptions, setLineOptions] = useState([]);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const siteTimezone = useMemo(
    () => resolveLineTimezone(configPayload, lineNo),
    [configPayload, lineNo]
  );

  useEffect(() => {
    if (!siteTimezone || datesTouched) {
      return;
    }
    const today = todayIso(siteTimezone);
    setDateFrom(`${today}${DAY_START}`);
    setDateTo(`${today}${DAY_END}`);
  }, [siteTimezone, datesTouched]);

  useEffect(() => {
    if (!auth.authChecked || !auth.isAuthenticated) {
      return;
    }
    let isMounted = true;
    const loadLines = async () => {
      try {
        const response = await auth.fetchWithAuth("/api/config");
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        const configs = Array.isArray(data?.line_configs) ? data.line_configs : [];
        const numbers = configs
          .map((item) => item?.line_no)
          .filter((value) => Number.isFinite(Number(value)))
          .map((value) => String(value));
        if (isMounted) {
          setConfigPayload(data);
          setLineOptions(numbers);
          setLineNo((current) => current || numbers[0] || "");
        }
      } catch (loadError) {
        console.warn("Failed to load line list:", loadError);
      }
    };
    loadLines();
    return () => {
      isMounted = false;
    };
  }, [auth]);

  const queryParams = useMemo(
    () => ({
      line_no: lineNo,
      date_from: withSeconds(dateFrom),
      date_to: withSeconds(dateTo),
      gap_threshold_seconds: gapThreshold,
    }),
    [lineNo, dateFrom, dateTo, gapThreshold]
  );

  const canSubmit = Boolean(lineNo && dateFrom && dateTo) && !loading;

  const loadReport = useCallback(async () => {
    if (!lineNo || !dateFrom || !dateTo) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await auth.fetchWithAuth("/api/ga-report", { params: queryParams });
      if (!response.ok) {
        const detail = await response.json().catch(() => null);
        throw new Error(detail?.detail || `Request failed: ${response.status}`);
      }
      setReport(await response.json());
    } catch (fetchError) {
      setError(String(fetchError.message || fetchError));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [auth, lineNo, dateFrom, dateTo, queryParams]);

  const downloadCsv = useCallback(async () => {
    setDownloading(true);
    setError("");
    try {
      const response = await auth.fetchWithAuth("/api/ga-report.csv", { params: queryParams });
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadName(
        response,
        `giveaway_report_line${lineNo}_${withSeconds(dateFrom)}_${withSeconds(dateTo)}.csv`.replace(/:/g, "-")
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      setError(String(downloadError.message || downloadError));
    } finally {
      setDownloading(false);
    }
  }, [auth, queryParams, lineNo, dateFrom, dateTo]);

  const rowsByDate = useMemo(() => {
    const rows = Array.isArray(report?.rows) ? report.rows : [];
    const grouped = new Map();
    rows.forEach((row) => {
      if (!grouped.has(row.date)) {
        grouped.set(row.date, []);
      }
      grouped.get(row.date).push(row);
    });
    return Array.from(grouped.entries());
  }, [report]);

  return (
    <AuthGate auth={auth} title="Giveaway Report Login">
      <div className="ga-page">
        <div className="ga-container">
          <header className="ga-header">
            <div>
              <h1 className="ga-title">Giveaway Report</h1>
              <p className="ga-subtitle">
                Per-SKU reconciliation · database vs scheduler
                {report?.timezone ? ` · local time ${report.timezone}` : ""}
              </p>
            </div>
            <div className="ga-header-meta">
              {report ? (
                <>
                  <span>
                    UTC window {report.window_utc?.start} → {report.window_utc?.end}
                  </span>
                  <span>
                    Orders read from scheduler totals:{" "}
                    {report.scheduler_attribution?.by_order_totals ?? 0}
                  </span>
                  <span>Heartbeats {report.heartbeat_rows ?? 0} · offset {report.single_pack_offset_grams} grams</span>
                  {report.unattributed_scheduler_rows > 0 ? (
                    <span className="ga-warn">
                      {report.unattributed_scheduler_rows} legacy scheduler rows outside any order
                    </span>
                  ) : null}
                </>
              ) : null}
            </div>
          </header>

          <form
            className="ga-controls"
            onSubmit={(event) => {
              event.preventDefault();
              loadReport();
            }}
          >
            <label>
              <span>Production line</span>
              <input
                type="number"
                list="ga-line-options"
                value={lineNo}
                min="1"
                onChange={(event) => setLineNo(event.target.value)}
                required
              />
              <datalist id="ga-line-options">
                {lineOptions.map((option) => (
                  <option key={option} value={option} />
                ))}
              </datalist>
            </label>
            <label>
              <span>Date from</span>
              <input
                type="datetime-local"
                step="1"
                value={dateFrom}
                onChange={(event) => {
                  setDatesTouched(true);
                  setDateFrom(event.target.value);
                }}
                required
              />
            </label>
            <label>
              <span>Date to</span>
              <input
                type="datetime-local"
                step="1"
                value={dateTo}
                onChange={(event) => {
                  setDatesTouched(true);
                  setDateTo(event.target.value);
                }}
                required
              />
            </label>
            <label>
              <span>Gap threshold (seconds)</span>
              <input
                type="number"
                min="1"
                value={gapThreshold}
                onChange={(event) => setGapThreshold(event.target.value)}
              />
            </label>
            <div className="ga-actions">
              <button type="submit" className="ga-button" disabled={!canSubmit}>
                {loading ? "Loading…" : "Show report"}
              </button>
              <button
                type="button"
                className="ga-button ga-button-secondary"
                onClick={downloadCsv}
                disabled={!canSubmit || downloading}
              >
                {downloading ? "Exporting…" : "Download CSV"}
              </button>
            </div>
          </form>

          <p className="ga-hint">
            Times are the site's local time{siteTimezone ? ` (${siteTimezone})` : ""}. Both ends
            are inclusive to the second, so 00:00:00 – 23:59:59 covers the whole day.
          </p>

          {error ? <div className="ga-error">{error}</div> : null}

          {report && rowsByDate.length === 0 && !loading ? (
            <div className="ga-empty">No packages for this line in the selected range.</div>
          ) : null}

          {rowsByDate.map(([day, rows]) => (
            <section className="ga-day" key={day}>
              <h2 className="ga-day-title">Report for: {day}</h2>
              <ReportTable rows={rows} />
            </section>
          ))}
        </div>
      </div>
    </AuthGate>
  );
}

export default GaReportPage;
